import type { MarketingTranslationKeys } from "../../types.js";

export const marketing: MarketingTranslationKeys = {
  meta: {
    defaultTitle: "HomeExpense — Financial clarity for your household",
    defaultDescription:
      "Manage household expenses with smart prorating, AI scanning, budgets, and a warranty vault.",
    featuresTitle: "Features — HomeExpense",
    featuresDescription:
      "Prorating, AI-powered OCR, expense journal, budgets, and a warranty vault for your home.",
    methodologyTitle: "Methodology — HomeExpense",
    methodologyDescription:
      "How we think about the product: precision, long-term stability, and a UI that keeps focus on the numbers.",
    legalTitle: "Legal & contact — HomeExpense",
    legalDescription:
      "Privacy, terms, security, and how to reach us. Transparency in how we handle your data.",
    privacyTitle: "Privacy policy — HomeExpense",
    privacyDescription:
      "Information about the data controller, data collected, purposes, retention, subprocessors, and your rights.",
    termsTitle: "Terms of use — HomeExpense",
    termsDescription:
      "General terms for the HomeExpense website and apps: account, acceptable use, intellectual property, and limitations.",
    pricingTitle: "Pricing — HomeExpense",
    pricingDescription:
      "Home, Smart scan, and Insights plans: features, AI limits, and reference pricing for households.",
  },
  nav: {
    ariaMain: "Main navigation",
    brand: "HomeExpense",
    home: "Home",
    features: "Features",
    methodology: "Methodology",
    pricing: "Pricing",
    legal: "Legal & contact",
    register: "Create account",
    login: "Sign in",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  footer: {
    tagline: "Clear household expense management.",
    platformTitle: "Product",
    legalTitle: "Legal",
    supportTitle: "Support",
    privacy: "Privacy",
    terms: "Terms",
    security: "Security",
    contact: "Contact",
    copyright: "© {{year}} HomeExpense. All rights reserved.",
  },
  images: {
    landingHeroAlt: "Minimal financial dashboard on a tablet, neutral tones with blue accents.",
    landingVizAlt: "Abstract financial data visualization in navy, emerald, and gold tones.",
    featuresHeroAlt: "Desk with financial stationery, natural light, clean lines.",
    featuresProrateAlt: "Tablet showing expense split charts and metrics.",
    featuresAiAlt: "Phone scanning a receipt with digital highlight lines.",
    legalMapAlt: "Stylized grayscale map of a city grid.",
    methodologyHeroAlt: "Minimal architectural detail, clean lines and soft shadows.",
    methodologyOfficeAlt: "Modern office interior with natural light and minimal furniture.",
  },
  home: {
    heroEyebrow: "Household finance",
    heroTitleLine1: "Financial clarity",
    heroTitleLine2: "for your home",
    heroDescription:
      "Smart prorating, an expense journal, budgets, AI scanning, and a warranty vault—built for households and shared living.",
    ctaPrimary: "Create a free account",
    ctaSecondary: "Explore our methodology",
    cardSurplusLabel: "Sample monthly cushion",
    cardSurplusValue: "€1,240.00",
    highlightsTitle: "Why HomeExpense",
    highlight1Label: "Focus",
    highlight1Value: "Shared households",
    highlight2Label: "Pricing",
    highlight2Value: "Free tier",
    highlight3Label: "Product",
    highlight3Value: "Continuous improvements",
    highlight4Label: "Privacy",
    highlight4Value: "Your data, your account",
    featuresEyebrow: "Core app areas",
    featuresTitle: "Features that match real workflows",
    featuresSubtitle:
      "The same pillars you will see in the app: expenses, budget, scanning, and vault—with fair splits across members.",
    featureProrateTitle: "Smart prorating",
    featureProrateDesc:
      "Allocate recurring and one-off costs across members, percentages, and household rules.",
    featureOcrTitle: "AI scanning",
    featureOcrDesc:
      "Upload receipts and let the system extract amount, merchant, and a suggested category for you to confirm.",
    featureVaultTitle: "Warranty vault",
    featureVaultDesc:
      "Track home assets with purchase and warranty dates, reminders, and organized documents.",
    visualizeTitle: "Budget visibility",
    visualizeSubtitle:
      "Progress bars and monthly views for reserved, spent, and category goals.",
    bar1Label: "Quarterly taxes (sample)",
    bar1Value: "65% reserved",
    bar2Label: "Renovation fund (sample)",
    bar2Value: "42% of goal",
    ctaBottomTitle: "Ready to start?",
    ctaBottomSubtitle: "Create your account in seconds and open the dashboard to set up your home.",
    ctaBottomButton: "Sign up free",
  },
  featuresPage: {
    heroEyebrow: "Product",
    heroTitle: "Tools for the modern",
    heroTitleItalic: "household",
    heroDescription:
      "More than a list: a journal with filters, prorated budgets, AI scanning, and a vault for warranties and documents.",
    f01Label: "Feature 01",
    f01Title: "Smart prorating",
    f01Body:
      "Align expenses with move-in dates, membership changes, and the rules you set for the home.",
    f01aTitle: "Date-aware logic",
    f01aBody: "Helpful when someone joins mid-month or contributions change.",
    f01bTitle: "Weighted splits",
    f01bBody: "Per-member percentages based on what the household agreed.",
    profileLabel: "Split profile (sample)",
    demoRowALabel: "Zone A",
    demoRowAPct: "42%",
    demoRowBLabel: "Zone B",
    demoRowBPct: "28%",
    demoRowCLabel: "Common areas",
    demoRowCPct: "30%",
    f02Label: "Feature 02",
    f02Title: "AI & OCR",
    f02Body:
      "Turn paper receipts into structured fields—amount, date, merchant, and a suggested category.",
    f02Quote:
      "The flow is designed so you can review and save in a few taps after scanning.",
    f02QuoteAttribution: "— HomeExpense team",
    overlayComplete: "Analysis complete",
    overlayCategoryLine: "Suggested category • detected amount",
    f03Eyebrow: "Feature 03",
    f03Title: "Warranty vault",
    f03Subtitle:
      "Register appliances and assets with dates, documents, and reminders before coverage ends.",
    vaultB1Title: "Expiry reminders",
    vaultB1Body: "Stay ahead of maintenance windows and warranty end dates.",
    vaultChip1: "Notifications",
    vaultChip2: "Email digests",
    vaultB2Title: "Claim-ready documentation",
    vaultB2Body: "Keep proof and asset details handy when you need them.",
    vaultB3Title: "Identifiable assets",
    vaultB3Body: "Clear references by name, type, and key dates.",
    vaultB4Title: "Linked history",
    vaultB4Body: "Attach service notes or invoices to the same asset record.",
    ctaTitle: "Get started today",
    ctaSubtitle: "Create your account and set up your home in minutes.",
    ctaPrimary: "Create account",
    ctaSecondary: "Read the methodology",
  },
  methodologyPage: {
    heroEyebrow: "Our methodology",
    heroTitle: "HomeExpense:",
    heroTitleAccent: "clarity on purpose",
    heroDescription:
      "Running a home is sequencing decisions over time. We prioritize reliable data, short flows, and UI that does not fight your numbers.",
    precisionTitle: "Precision",
    precisionBody:
      "We avoid shortcuts that distort totals—categories, splits, and the household month stay consistent in UI and API.",
    accuracyLabel: "Focus",
    accuracyValue: "Consistent data",
    stabilityTitle: "Stability",
    stabilityBody:
      "Budget and journal look at the month and the near future: recurring charges, prorating, and household balance.",
    growthTitle: "Big picture",
    growthSubtitle: "Summaries that support the next decision.",
    riskTitle: "Fewer surprises",
    riskSubtitle: "Warranties and reminders for obligations and assets.",
    narrativeEyebrow: "Experience",
    narrativeQuote:
      "A home is a system—people, expenses, and time. The app is where that system becomes visible.",
    narrativeManifesto: "Product manifesto",
    pillar1Num: "01",
    pillar1Title: "Quiet UI",
    pillar1Body: "Clear hierarchy so the important number carries the visual weight.",
    pillar2Num: "02",
    pillar2Title: "Data with narrative",
    pillar2Body: "Lists, totals, and bars that explain what happened and what is next.",
    pillar3Num: "03",
    pillar3Title: "Household tooling",
    pillar3Body: "Homes, members, budget, vault, and scanning in one flow.",
    breakTitle: "Built for whoever runs the household finances.",
    breakCta: "Explore features",
  },
  legalPage: {
    heroEyebrow: "Commitment",
    heroTitle: "Privacy, security, and",
    heroTitleAccent: "open conversation",
    heroDescription:
      "Find links to our privacy policy and terms of use, security information, and how to reach us.",
    controllerName: "GProjectdevelop LLC (United States).",
    controllerTaxNote:
      "The company is in the process of being incorporated; definitive tax identifiers will be published here when available.",
    controllerPresence:
      "Distributed team with presence in Miami (US) and Asturias (Spain), without a single public-facing office address.",
    privacyTitle: "Privacy",
    privacyLead:
      "We do not sell your financial data. Encryption in transit and sound storage practices are the baseline.",
    privacyBullet1: "HTTPS for client traffic.",
    privacyBullet2: "Household spaces require your authenticated account.",
    privacyBullet3: "Right to request deletion under applicable law.",
    privacyReadMore: "Read the full privacy policy",
    termsSectionLabel: "Agreement",
    termsTitle: "Terms of service",
    termsBody:
      "These terms govern access to and use of the HomeExpense website and apps. Please read the full document.",
    termsUpdatedLabel: "Last updated",
    termsUpdatedValue: "May 10, 2026",
    termsCta: "View terms of use",
    securityTitle: "Security",
    securityBody:
      "Infrastructure and practices aligned with common SaaS expectations, strengthened where API and auth require it.",
    securityChip1: "HTTPS",
    securityChip2: "Secure authentication",
    contactTitle: "Contact",
    contactLead:
      "For support, privacy, or data questions, email us or use the form; data-protection enquiries may use the same channel.",
    contactMapTitle: "Presence",
    contactMapBody:
      "Distributed team across Miami (Florida, US) and Asturias (Spain). We do not operate a single walk-in office address.",
    formName: "Full name",
    formNamePlaceholder: "Your name",
    formEmail: "Email",
    formEmailPlaceholder: "you@email.com",
    formMessage: "Message",
    formMessagePlaceholder: "How can we help?",
    formSubmit: "Send message",
    formSubmitting: "Sending…",
    formSuccess: "Message sent. We will get back to you shortly.",
    formErrorGeneric: "We could not send your message. Please try again later.",
    formErrorValidation: "Please check your name, email, and message (minimum 10 characters).",
    formErrorConfig: "Form delivery is not configured in this environment.",
  },
  privacyPage: {
    documentEyebrow: "Transparency",
    documentTitle: "Privacy policy",
    lastUpdatedLabel: "Last updated",
    lastUpdatedValue: "May 10, 2026",
    intro:
      "HomeExpense takes privacy seriously. This policy describes, in general terms, what personal data we may process when you use our website or mobile apps, for what purposes, and what rights you have. The service may evolve; if you reside in the European Economic Area or other regions with specific rules, some points may require additional detail that you can request through the channels at the end of this page.",
    sectionControllerTitle: "1. Data controller",
    sectionControllerBody:
      "The controller of personal data obtained through the HomeExpense product, in relation to users and visitors of our official channels, is GProjectdevelop LLC, domiciled in the United States (“we”, “us”, or the “controller”). The entity may still be in the process of incorporation; registration details and definitive tax identifiers will be updated in this policy when applicable. For any matter related to this policy or to exercising your rights, you can write to the email address in the contact section (including data-protection contact where relevant).",
    sectionDataTitle: "2. Personal data we may collect",
    sectionDataIntro:
      "Depending on how you interact with the service, we may process categories such as the following (not all will apply in every case):",
    sectionDataBullet1:
      "Account and profile data: identifier, name or alias, email address, language or time-zone preferences, and access credentials (handled with appropriate security measures).",
    sectionDataBullet2:
      "Household and financial data you or authorised members enter: expenses, budgets, categories, documents or metadata linked to the vault, and information you choose to store in the app.",
    sectionDataBullet3:
      "Technical and app data: device type, operating system, app version, technical or session identifiers, error logs, and, where applicable, aggregated usage data to improve service stability.",
    sectionDataBullet4:
      "Communications you voluntarily send us (for example support messages) and, if needed, metadata required to handle the conversation.",
    sectionDataBullet5:
      "Images or files you upload for features such as receipt scanning; they may include data visible on the document. Avoid including unnecessary sensitive data.",
    sectionPurposeTitle: "3. Purposes",
    sectionPurposeBody:
      "We process data to provide and maintain the HomeExpense service (households, expenses, budget, vault, and related features), authenticate users, personalise the in-account experience, provide support, comply with applicable legal obligations, improve security and performance, and communicate with you about the service when needed. If we introduce optional marketing communications in the future, we will, where required, ask for consent or offer a clear opt-out in line with applicable law.",
    sectionLegalBasisTitle: "4. Legal bases (general framework)",
    sectionLegalBasisBody:
      "Depending on the processing and the law that applies (for example the EU General Data Protection Regulation, “GDPR”), legal bases may include: performance of the contract or service terms when processing is necessary to deliver the functionality you request; our legitimate interests in ensuring security, technical improvement, and fraud prevention, balanced against your rights; compliance with legal obligations; and consent where required for specific purposes (for example certain non-essential cookies or optional communications). You can ask for more detail about a specific processing activity using the contact details below.",
    sectionRetentionTitle: "5. Retention",
    sectionRetentionBody:
      "We keep data for as long as needed to fulfil the purposes described and any legal obligations. Criteria include the life of your account, the need to resolve incidents or claims, and statutory limitation or audit periods. When data is no longer needed, we delete or anonymise it in line with internal procedures and applicable law.",
    sectionThirdPartiesTitle: "6. Processors and third-party services",
    sectionThirdPartiesBody:
      "We may use providers for infrastructure, hosting, transactional email, error monitoring, or other tools that act as processors on our behalf, with appropriate agreements and security measures. We may also have to disclose data to authorities if required by law. We do not sell your personal data. If we transfer data outside the European Economic Area, we will use recognised safeguards (for example standard contractual clauses) where appropriate.",
    sectionRightsTitle: "7. Your rights",
    sectionRightsIntro:
      "To the extent they apply against the controller, you may exercise the following rights (subject to legal exceptions):",
    sectionRightsBullet1: "Access to your personal data and information about the processing.",
    sectionRightsBullet2: "Rectification of inaccurate or incomplete data.",
    sectionRightsBullet3: "Erasure (“right to be forgotten”) where applicable.",
    sectionRightsBullet4: "Restriction of processing, objection, and, where applicable, portability of data you have provided.",
    sectionRightsBullet5:
      "Withdraw consent where processing is based on it, without affecting lawfulness before withdrawal. You may also lodge a complaint with the data protection authority in your country.",
    sectionMinorsTitle: "8. Children",
    sectionMinorsBody:
      "HomeExpense is not directed at children under 16. If you are a parent or guardian and believe a child has provided personal data without your consent, contact us so we can take appropriate steps.",
    sectionChangesTitle: "9. Changes to this policy",
    sectionChangesBody:
      "We may update this policy to reflect legal, technical, or product changes. We will publish the current version on this page and, when a change is material, we will try to notify you by reasonable means (for example an in-app notice or email). We encourage you to review the policy periodically.",
    sectionContactTitle: "10. Contact",
    sectionContactBody:
      "To exercise your rights, ask privacy questions, or request further information about how we process your data, contact us at:",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  termsPage: {
    documentEyebrow: "Terms",
    documentTitle: "Terms of use",
    lastUpdatedLabel: "Last updated",
    lastUpdatedValue: "May 10, 2026",
    intro:
      "These terms govern access to and use of the website at home-expense.gprojectdevelop.com, the HomeExpense mobile apps, and related services offered by GProjectdevelop LLC (“we”, “the provider”). By creating an account, downloading the app, or using the service, you confirm that you have read and accept these terms. If you do not agree, do not use the service. This is an initial draft for transparency; legal review is recommended for your jurisdiction and business model.",
    sectionPartyTitle: "1. Parties and status",
    sectionPartyBody:
      "The service is provided by GProjectdevelop LLC, domiciled in the United States. Corporate and tax information may be updated on these pages when finalised. “User” means you, an individual or the entity on whose behalf you act, using the service.",
    sectionServiceTitle: "2. Description of the service",
    sectionServiceBody:
      "HomeExpense provides digital tools to organise household expenses, budgets, members, a document vault, and auxiliary features (including, depending on your plan, AI capabilities). The service is provided “as is” and may change: new features, limits, maintenance, or temporary outages. We are not financial, tax, or legal advisers; calculations and suggestions are informational and do not replace professional advice.",
    sectionAccountTitle: "3. Account, age, and security",
    sectionAccountBody:
      "You must provide accurate registration information and keep your credentials confidential. You are responsible for activity on your account unless you demonstrate unauthorised access reported without undue delay. The service is not directed at children under 16; if you are a child, you must not register. We may refuse registration or suspend or close accounts that breach these terms or the law.",
    sectionAcceptableUseTitle: "4. Acceptable use",
    sectionAcceptableUseIntro: "You agree not to use the service to:",
    sectionAcceptableUseBullet1:
      "Violate applicable laws, infringe third-party rights, or access systems or data without authorisation.",
    sectionAcceptableUseBullet2:
      "Introduce malware, disproportionately reverse engineer, probe vulnerabilities without authorisation, or overload infrastructure.",
    sectionAcceptableUseBullet3:
      "Resell, sublicense, or exploit the service in a way that competes with the product without written agreement.",
    sectionAcceptableUseBullet4:
      "Upload unlawful, offensive content, or third-party personal data without an appropriate legal basis.",
    sectionContentTitle: "5. Your data and content",
    sectionContentBody:
      "You retain ownership of information you enter (expenses, documents, etc.). You grant us a limited, non-exclusive, worldwide licence to host, process, encrypt in transit, and display that data to provide the service, including processing required for AI features if you enable them. Processing of personal data is described in the privacy policy. You are responsible for the lawfulness of data you upload.",
    sectionFeesTitle: "6. Plans, pricing, and billing",
    sectionFeesBody:
      "Some features may be free and others paid according to the plan published on the site or in the app. Prices may change with reasonable notice where permitted by law. Taxes and third-party charges (carriers, app stores) may apply. If we add in-app payments in the future, additional terms shown at purchase will apply.",
    sectionIpTitle: "7. Intellectual property",
    sectionIpBody:
      "Software, branding, design, and service materials (excluding your content) belong to the provider or its licensors. You do not acquire rights in them except as needed to use the service under these terms.",
    sectionDisclaimerTitle: "8. Disclaimer of warranties",
    sectionDisclaimerBody:
      "To the fullest extent permitted by law, the service is provided without express or implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee economic results or error-free automated extraction (OCR/AI).",
    sectionLiabilityTitle: "9. Limitation of liability",
    sectionLiabilityBody:
      "To the maximum extent permitted by law, we will not be liable for indirect damages, lost profits, data loss, or consequential damages. Our aggregate liability for claims relating to the service in any twelve (12) month period will not exceed the fees you paid for paid services in that period or, if none, fifty euros (€50) or the local equivalent. Some jurisdictions do not allow certain limitations; in that case the legal minimum applies.",
    sectionIndemnityTitle: "10. Indemnity",
    sectionIndemnityBody:
      "You will indemnify us against third-party claims arising from your misuse of the service, your content, or breach of these terms, including reasonable attorneys’ fees.",
    sectionTerminationTitle: "11. Termination and suspension",
    sectionTerminationBody:
      "You may stop using the service and request account closure via product flows or by email. We may suspend or terminate access for serious breaches, security risk, or legal requirement. After closure, we may delete data in line with the privacy policy and legal obligations.",
    sectionLawTitle: "12. Governing law and disputes",
    sectionLawBody:
      "Unless mandatory rules in your country of residence provide otherwise, these terms are governed by the laws of the United States and, for disputes relating to services provided from Florida, the laws of the State of Florida, without regard to conflict-of-law rules. Competent courts will be those consistent with that choice, without prejudice to non-waivable consumer rights in the EU or other regions.",
    sectionChangesTitle: "13. Changes",
    sectionChangesBody:
      "We may amend these terms by posting a new version on this page and updating the “Last updated” date. For material changes we will try to notify you by reasonable means. Continued use after the effective date may constitute acceptance; if you do not agree, you must stop using the service.",
    sectionContactTitle: "14. Contact",
    sectionContactBody:
      "For questions about these terms or the service, write to:",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  pricingPage: {
    heroEyebrow: "Plans",
    heroTitle: "Straightforward pricing",
    heroSubtitle:
      "Three tiers: core without AI, AI receipt scanning with a daily cap, and smart insights with more scans. Online billing will roll out later.",
    leadTitle: "Transparency",
    leadBody:
      "Paid tiers reflect estimated AI processing cost plus a modest margin. The Home plan stays free.",
    noPaymentNote: "In-app payments are not enabled yet—you can sign up and use the product without a checkout flow.",
    priceNote: "Reference prices; taxes may apply based on your country.",
    plan1Name: "Home",
    plan1Tagline: "Full core product, no AI features",
    plan1Price: "€0",
    plan1Period: "/month",
    plan1RecurringCaption: "Free forever",
    plan1F1: "Expense journal, prorating, budget, and warranty vault",
    plan1F2: "Household members and customizable split rules",
    plan1F3: "No AI receipt scanning or automated suggestions",
    plan2Name: "Smart scan",
    plan2Tagline: "Home plus AI scanning",
    plan2Price: "€4.99",
    plan2Period: "/month",
    plan2RecurringCaption: "Reference monthly price",
    plan2Popular: "Best balance",
    plan2F1: "Everything in Home",
    plan2F2: "Up to 5 AI receipt scans per day",
    plan2F3: "Amount, merchant, and suggested category extraction",
    plan3Name: "Insights",
    plan3Tagline: "More AI and financial nudges",
    plan3Price: "€12.99",
    plan3Period: "/month",
    plan3RecurringCaption: "Reference monthly price",
    plan3F1: "Everything in Smart scan",
    plan3F2: "Up to 15 AI scans per day",
    plan3F3: "Smart suggestions on spending patterns and opportunities",
    cta: "Create account",
    includesLabel: "Includes",
  },
};
